/**
 * Sprite factory + code-driven animation helpers (art-pipeline "Path C").
 *
 * Ships with static single-pose sprites animated purely by code (idle bob,
 * attack lunge, hurt flash, shadow blob). Designed so swapping to real
 * frame-based animation later is a config change, not a rewrite: callers ask
 * for a "character" and get back a controller with the same API regardless of
 * whether it's a single image or an animated atlas.
 *
 * No game logic here — this only reflects state the renderer is given.
 */

/**
 * Code-driven animation profiles for static monster sprites.
 * Higher tiers get more elaborate idle behaviours.
 *   float        — gentle vertical bob (default for every static mob)
 *   float_deep   — wider, slower bob (big heavy creatures)
 *   float_wisp   — narrow fast bob + alpha pulse (ethereal/ghost)
 *   float_coil   — bob + slow rotation oscillation (serpents)
 *   float_pulse  — bob + scale breathing (titans/golems)
 */
/**
 * Creatures whose source art is already drawn facing LEFT (toward the heroes).
 * Every other monster is drawn facing right and gets flipped; these must NOT be
 * flipped or they'd turn their back to the party. (The Drowned Depths trio came
 * out left-facing from the art model; the leviathan boss faces right.)
 */
const FACES_LEFT = new Set(['abyss_angler', 'drowned_knight', 'deep_lurker']);

/**
 * Ground anchor (origin.y, 0..1) per TEXTURE for static sprites whose support
 * line (paws/base) isn't the bbox bottom — tails, wisps or baked shadows dangle
 * lower. Keyed by texture (not monster id) because the attack pose usually has
 * a completely different support line than the idle pose; the texture swap
 * during a lunge must swap the anchor too or the creature pops up/down.
 * Values are MEASURED by tools/fix_static_sprites.py (lowest row whose opaque
 * run spans >=28% of the sprite width), not guessed. Unlisted textures = 1.
 */
const TEX_GROUND_ORIGIN = {
  mob_rat: 0.976,
  mob_bird: 0.805,
  mob_bird_atk: 0.789,
  mob_wolf_atk: 0.731,
  mob_alpha_wolf: 0.942,
  mob_alpha_wolf_atk: 0.904,
  mob_storm_harpy: 0.917,
  mob_seraph_sentinel: 0.917,
  mob_celestial_archon: 0.969,
  mob_astral_serpent: 0.927,
  mob_cosmic_devourer: 0.915,
  mob_star_titan: 0.969,
  mob_plague_lich: 0.877,
  mob_demon_lord: 0.969,
  mob_crystal_shardling: 0.923,
  mob_prism_stalker: 0.892,
  mob_bone_scorpion: 0.958,
  mob_rot_behemoth: 0.958,
  mob_brass_spider: 0.854,
};

/**
 * Hover height (px above the ground line) for creatures that FLY or float by
 * nature. Their sprite rides this much above the baseline while the shadow
 * stays on the grass (smaller + lighter, since the caster is further away) —
 * that contrast is what makes hovering read as intentional instead of a bug.
 * Grounded creatures are absent (0): feet planted on the ground line.
 */
const HOVER = {
  bird: 26, // small flyer — ride clearly above the grass so it reads as flying
  storm_harpy: 13,
  seraph_sentinel: 10,
  celestial_archon: 8,
  nebula_wraith: 10,
  astral_serpent: 6,
  cosmic_devourer: 8,
  star_titan: 6,
  plague_lich: 9,
  wraith_king: 9,
  crystal_shardling: 10,
  sand_wraith: 10,
};

const STATIC_ANIM = {
  // Tier 1 — Campos Verdejantes (hand-drawn static sprites + code lunge)
  rat:              'ground',       // paws planted — breathing, no hover
  bird:             'float_wisp',   // airborne raven — hovers (see HOVER)
  wolf:             'ground',       // grounded beast — breathing
  alpha_wolf:       'ground_heavy', // boss wolf — slow deep breathing
  // Tier 2 — Floresta Sombria (frame-animated now; kept as fallback only)
  spider:           'ground',
  bandit:           'ground',
  treant:           'ground_heavy',
  elder_treant:     'ground_heavy',
  // Tier 7 — Celestial Throne
  storm_harpy:      'float_deep',   // flying — hovers
  astral_golem:     'ground_heavy', // stone colossus — planted, breathing
  seraph_sentinel:  'float_wisp',   // ethereal angel — hovers
  celestial_archon: 'float_pulse',  // celestial boss — hovers
  // Tier 8 — Vazio Estelar
  nebula_wraith:    'float_wisp',
  astral_serpent:   'float_coil',
  cosmic_devourer:  'float_pulse',
  star_titan:       'float_deep',
  // Tier 9 — Necrópole Eterna
  bone_knight:      'ground',       // walking undead — planted
  plague_lich:      'float_wisp',   // floating caster — hovers
  wraith_king:      'float_wisp',   // spectral — hovers
  death_sovereign:  'ground_heavy', // towering boss — planted, heavy breath
  // Tier 10 — Inferno Carmesim
  hellhound:        'ground',
  flame_demon:      'ground_heavy', // burning hulk — planted, breathing
  dread_knight:     'ground',
  demon_lord:       'ground_heavy',
  // Tier 11 — Caverna Cristalina
  crystal_shardling:'float_wisp',   // floating shards — hovers
  prism_stalker:    'ground',
  geode_brute:      'ground_heavy',
  crystal_monarch:  'ground_heavy',
  // Tier 12 — Deserto Maldito
  sand_wraith:      'float_wisp',   // floating spirit — hovers
  bone_scorpion:    'ground',
  tomb_guardian:    'ground',
  cursed_pharaoh:   'ground_heavy',
  // Tier 13 — Pântano Putrefato
  bog_hag:          'ground',
  plague_toad:      'ground_heavy', // bloated — deep breathing
  rot_behemoth:     'ground_heavy',
  swamp_horror:     'ground_heavy',
  // Tier 14 — Cidadela Mecânica
  clockwork_soldier:'ground',
  brass_spider:     'ground',
  steam_golem:      'ground_heavy',
  mechanical_overlord:'ground_heavy',
};

/**
 * @typedef {object} CharController
 * @property {Phaser.GameObjects.Container} container the root display object
 * @property {(direction:string)=>void} face switch facing (front/side/back)
 * @property {()=>void} attack play a one-shot attack lunge
 * @property {()=>void} hurt play a one-shot hurt flash
 * @property {(scale:number)=>void} setScale uniform scale of the sprite
 */

/**
 * Build an animated character on top of static directional sprites.
 *
 * @param {Phaser.Scene} scene
 * @param {number} x
 * @param {number} y baseline (feet) position
 * @param {string} classId e.g. "knight"; expects textures `${classId}_front` etc.
 * @param {object} [opts]
 * @param {number} [opts.scale=1] uniform scale (ignored if targetHeight set)
 * @param {number} [opts.targetHeight] desired on-screen height in px
 * @param {boolean} [opts.flipForLeft=true] flip the side pose to face left
 * @returns {CharController}
 */
export function createCharacter(scene, x, y, classId, opts = {}) {
  const { targetHeight, flipForLeft = true } = opts;

  const container = scene.add.container(x, y);

  // The sprite itself, anchored at its feet (bottom-center).
  const key = textureFor(scene, classId, 'front');
  const sprite = scene.add.image(0, 0, key);
  sprite.setOrigin(0.5, 1);
  const scale = targetHeight ? targetHeight / sprite.height : (opts.scale ?? 1);
  sprite.setScale(scale);

  // Soft elliptical shadow blob under the feet, sized to the sprite.
  const shadow = scene.add.ellipse(0, 2, sprite.displayWidth * 0.7, 12, 0x000000, 0.35);
  container.add(shadow);
  container.add(sprite);

  // Idle bob: a gentle vertical sine on the sprite (shadow stays put).
  scene.tweens.add({
    targets: sprite,
    y: -4 * scale,
    duration: 900,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });

  /** @type {CharController} */
  const controller = {
    container,
    face(direction) {
      let dir = direction;
      let flip = false;
      if (direction === 'left' || direction === 'right') {
        dir = 'side';
        flip = flipForLeft && direction === 'left';
      }
      sprite.setTexture(textureFor(scene, classId, dir));
      sprite.setFlipX(flip);
    },
    attack() {
      const dx = (sprite.flipX ? -1 : 1) * 10 * scale;
      scene.tweens.add({
        targets: sprite,
        x: dx,
        duration: 110,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
    },
    hurt() {
      sprite.setTintFill(0xffffff);
      scene.time.delayedCall(90, () => sprite.clearTint());
    },
    setScale(s) {
      sprite.setScale(s);
    },
  };

  return controller;
}

/**
 * @typedef {object} AnimHeroController
 * @property {Phaser.GameObjects.Sprite} sprite
 * @property {Phaser.GameObjects.Container} container
 * @property {()=>void} walk loop the walk cycle
 * @property {()=>void} attack play one attack swing, then resume walk
 * @property {()=>void} hurt white flash
 * @property {(faceLeft:boolean)=>void} face
 */

/**
 * Build a frame-animated hero (walk loop + attack one-shot) from individual
 * frame textures. This is the "swap to real frames" path the static factory was
 * designed to grow into — the rest of the game stays animation-agnostic.
 *
 * @param {Phaser.Scene} scene
 * @param {number} x
 * @param {number} y baseline (feet) position
 * @param {object} cfg
 * @param {string} cfg.id e.g. "knight"
 * @param {string[]} cfg.walk walk frame texture keys in order
 * @param {string[]} cfg.attack attack frame texture keys in order
 * @param {number} [cfg.targetHeight=110]
 * @returns {AnimHeroController}
 */
export function createAnimatedCharacter(scene, x, y, cfg) {
  const { id, walk, attack, targetHeight = 110 } = cfg;
  const walkKey = `${id}_walk`;
  const attackKey = `${id}_attack`;

  if (!scene.anims.exists(walkKey)) {
    scene.anims.create({
      key: walkKey,
      frames: walk.map((key) => ({ key })),
      frameRate: 8,
      repeat: -1,
    });
  }
  if (!scene.anims.exists(attackKey)) {
    // Skip frame 0 (wind-up behind body) — start at frame 1 for cleaner swing.
    const attackFrames = attack.length > 2 ? attack.slice(1) : attack;
    scene.anims.create({
      key: attackKey,
      frames: attackFrames.map((key) => ({ key })),
      frameRate: 18,
      repeat: 0,
    });
  }

  const container = scene.add.container(x, y);
  const sprite = scene.add.sprite(0, 0, walk[0]);
  sprite.setOrigin(0.5, 1);
  sprite.setScale(targetHeight / sprite.height);
  const shadow = scene.add.ellipse(0, 2, sprite.displayWidth * 0.6, 12, 0x000000, 0.38);
  container.add([shadow, sprite]);
  sprite.play(walkKey);

  let attacking = false;
  let flashTimer = null;
  sprite.on('animationcomplete', (anim) => {
    if (anim.key === attackKey) {
      attacking = false;
      sprite.play(walkKey);
    }
  });

  return {
    sprite,
    container,
    walk() {
      if (!attacking) sprite.play(walkKey, true);
    },
    attack() {
      attacking = true;
      sprite.play(attackKey);
    },
    hurt() {
      sprite.setAlpha(0.35);
      if (flashTimer) { flashTimer.remove(false); flashTimer = null; }
      flashTimer = scene.time.delayedCall(85, () => { sprite.setAlpha(1); flashTimer = null; });
    },
    face(faceLeft) {
      sprite.setFlipX(!!faceLeft);
    },
  };
}

/**
 * @typedef {object} MonsterController
 * @property {Phaser.GameObjects.Container} container
 * @property {(hp:number, maxHp:number)=>void} setHp update the HP bar
 * @property {()=>void} attack lunge toward the hero (leftward)
 * @property {()=>void} hurt white flash
 * @property {(onDone:()=>void)=>void} die death animation then callback
 */

/**
 * Build an animated enemy with walk loop + attack one-shot, falling back to a
 * static sprite with code-driven tweens when frame textures aren't loaded.
 *
 * Frame naming convention (facing right, flipped to face left in code):
 *   mob_{monsterId}_walk_0..3   mob_{monsterId}_attack_0..3
 *
 * @param {Phaser.Scene} scene
 * @param {number} x
 * @param {number} y baseline (feet) position
 * @param {string} monsterId e.g. "rat", "wolf", "spider"
 * @param {object} [opts]
 * @param {number} [opts.targetHeight=96]
 * @param {boolean} [opts.boss=false]
 * @param {string} [opts.fallbackTexture] static texture key used when no frames exist
 * @returns {MonsterController}
 */
export function createMonster(scene, x, y, monsterId, opts = {}) {
  const { targetHeight = 96, boss = false, elite = false, fallbackTexture } = opts;
  // Elites are a touch bigger than a normal mob (but never bigger than a boss).
  const height = boss ? targetHeight * 1.25 : (elite ? targetHeight * 1.12 : targetHeight);

  // Detect whether frame-based animation assets are loaded.
  const walkFrameKeys = [0, 1, 2, 3].map((i) => `mob_${monsterId}_walk_${i}`);
  const attackFrameKeys = [0, 1, 2, 3].map((i) => `mob_${monsterId}_attack_${i}`);
  const hasWalk = walkFrameKeys.every((k) => scene.textures.exists(k));
  const hasAttack = attackFrameKeys.every((k) => scene.textures.exists(k));

  const walkAnimKey = `mob_${monsterId}_walk`;
  const attackAnimKey = `mob_${monsterId}_attack`;

  if (hasWalk && !scene.anims.exists(walkAnimKey)) {
    scene.anims.create({
      key: walkAnimKey,
      frames: walkFrameKeys.map((key) => ({ key })),
      frameRate: 6,
      repeat: -1,
    });
  }
  if (hasAttack && !scene.anims.exists(attackAnimKey)) {
    scene.anims.create({
      key: attackAnimKey,
      frames: attackFrameKeys.map((key) => ({ key })),
      frameRate: 12,
      repeat: 0,
    });
  }

  const container = scene.add.container(x, y);
  let sprite;
  let bob = null;
  let attacking = false;
  let flashTimer = null;
  // Optional second "attack pose" texture for static mobs: swapped in during the
  // lunge so the strike reads as a real attack (idle ↔ attack), then swapped back.
  let idleTex = null;
  let atkTex = null;
  let idleScale = 1;
  let atkScale = 1;

  if (hasWalk) {
    sprite = scene.add.sprite(0, 0, walkFrameKeys[0]);
    sprite.setOrigin(0.5, 1);
    sprite.setScale(height / sprite.height);
    sprite.setFlipX(!FACES_LEFT.has(monsterId)); // most art faces right → flip to face the heroes
    sprite.play(walkAnimKey);
    sprite.on('animationcomplete', (anim) => {
      if (anim.key === attackAnimKey) {
        attacking = false;
        sprite.play(walkAnimKey);
      }
    });
  } else {
    // Static fallback with code-driven animation profile.
    const texKey = fallbackTexture && scene.textures.exists(fallbackTexture)
      ? fallbackTexture
      : `mob_${monsterId}`;
    const resolved = scene.textures.exists(texKey) ? texKey : '__missing';
    sprite = scene.add.image(0, 0, resolved);
    sprite.setOrigin(0.5, TEX_GROUND_ORIGIN[resolved] ?? 1);
    sprite.setScale(height / sprite.height);
    sprite.setFlipX(!FACES_LEFT.has(monsterId));

    // Flying/floating creatures ride above the ground line; their idle tweens
    // oscillate around this baseline instead of around the ground itself.
    const hover = HOVER[monsterId] ?? 0;
    sprite.y = -hover;

    // Wire an attack-pose texture if one was loaded (mob_<id>_atk), scaling it
    // to the same on-screen height as the idle pose so the swap doesn't pop.
    // The attack pose gets its own measured ground anchor (see attack()).
    idleTex = resolved;
    idleScale = sprite.scaleX;
    const atkKey = `mob_${monsterId}_atk`;
    if (scene.textures.exists(atkKey)) {
      atkTex = atkKey;
      const ah = scene.textures.get(atkKey).getSourceImage()?.height || sprite.height;
      atkScale = height / ah;
    }

    const animStyle = STATIC_ANIM[monsterId] ?? 'ground';
    const extraTweens = [];
    const s = sprite.scaleX;

    if (animStyle === 'ground') {
      // Planted feet, breathing torso: scaleY squash around the bottom origin.
      // No vertical motion — vertical bob on a grounded creature reads as
      // floating (the exact bug this replaced).
      bob = scene.tweens.add({ targets: sprite, scaleY: s * 1.03, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    } else if (animStyle === 'ground_heavy') {
      // Slower, deeper breathing for hulks and grounded bosses.
      bob = scene.tweens.add({ targets: sprite, scaleY: s * 1.045, duration: 1350, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    } else if (animStyle === 'float_deep') {
      // Wide slow float — big flyers (harpies, titans).
      bob = scene.tweens.add({ targets: sprite, y: -hover - 7, duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    } else if (animStyle === 'float_wisp') {
      // Narrow fast bob + alpha pulse — ethereal / ghost types.
      bob = scene.tweens.add({ targets: sprite, y: -hover - 4, duration: 650, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      extraTweens.push(scene.tweens.add({ targets: sprite, alpha: 0.65, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' }));
    } else if (animStyle === 'float_coil') {
      // Gentle bob + slow rotation oscillation — serpents.
      bob = scene.tweens.add({ targets: sprite, y: -hover - 5, duration: 1000, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      extraTweens.push(scene.tweens.add({ targets: sprite, angle: 6, duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' }));
    } else if (animStyle === 'float_pulse') {
      // Bob + scale breathing — floating cosmic entities.
      bob = scene.tweens.add({ targets: sprite, y: -hover - 4, duration: 950, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      extraTweens.push(scene.tweens.add({ targets: sprite, scaleX: s * 1.04, scaleY: s * 1.04, duration: 1100, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' }));
    } else {
      // 'float' — legacy gentle bob (only used if a new id ships unmapped).
      bob = scene.tweens.add({ targets: sprite, y: -hover - 3, duration: 820, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }

    // Stop all idle tweens on die (captured via closure).
    sprite._extraTweens = extraTweens;
  }

  // Contact shadow. A hovering creature casts a smaller, fainter one (it's
  // further from the ground) — that distance is what sells the hover.
  const hovering = !hasWalk && (HOVER[monsterId] ?? 0) > 0;
  const shadow = scene.add.ellipse(
    0, 2,
    sprite.displayWidth * (hovering ? 0.45 : 0.7),
    hovering ? 8 : 12,
    0x000000, hovering ? 0.22 : 0.35,
  );

  // HP bar above the head (hovering creatures ride higher, so does the bar).
  const barW = 44;
  const top = -height - 8 - (hovering ? (HOVER[monsterId] ?? 0) : 0);
  const barBg = scene.add.rectangle(0, top, barW, 6, 0x000000, 0.6).setStrokeStyle(1, 0x3a3147);
  const barFill = scene.add.rectangle(-barW / 2, top, barW, 4, (boss || elite) ? 0xff8000 : 0x46d160).setOrigin(0, 0.5);

  container.add([shadow, sprite, barBg, barFill]);

  // Level tag beside the HP bar — makes the in-zone scaling VISIBLE: monsters
  // literally level up with you inside a zone, and this is how players see it.
  if (opts.level) {
    const lvlText = scene.add.text(-barW / 2 - 3, top, `Nv.${opts.level}`, {
      fontFamily: 'monospace', fontSize: '9px', color: boss ? '#ffb35c' : '#cfc4a8',
      stroke: '#1a1208', strokeThickness: 2,
    }).setOrigin(1, 0.5);
    container.add(lvlText);
  }

  // Elite marker: a gold ★ that hovers just above the HP bar, with a soft pulse
  // so the rare tougher variant reads at a glance.
  if (elite) {
    const star = scene.add.text(0, top - 9, '★', {
      fontFamily: 'Arial, sans-serif', fontSize: '15px', color: '#ffd24a',
      stroke: '#3a2600', strokeThickness: 3,
    }).setOrigin(0.5, 1);
    container.add(star);
    scene.tweens.add({ targets: star, scaleX: 1.2, scaleY: 1.2, duration: 620, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
  }

  // Scale-only pop-in — no alpha fade so fast-kill monsters are never invisible.
  scene.tweens.add({
    targets: container,
    scaleX: { from: 0.6, to: 1 },
    scaleY: { from: 0.6, to: 1 },
    duration: 180,
    ease: 'Back.easeOut',
  });

  return {
    container,
    setHp(hp, maxHp) {
      const pct = Phaser.Math.Clamp(maxHp ? hp / maxHp : 0, 0, 1);
      scene.tweens.add({ targets: barFill, scaleX: pct, duration: 150 });
    },
    attack() {
      if (hasWalk && hasAttack && !attacking) {
        attacking = true;
        sprite.play(attackAnimKey);
        return;
      }
      // Static creatures have a single pose, so fake a strike: a quick wind-up
      // (lean back, away from the heroes) then a fast lunge toward them and a
      // recoil — reads as an attack instead of just bobbing. (All monsters face
      // left toward the party, so the lunge is always -x.) Skip x-only so it
      // doesn't fight the idle bob (y) or breathing (scaleX).
      if (attacking) return;
      attacking = true;
      // Swap to the attack pose (if present) for the duration of the lunge.
      // Pause the idle tweens (they animate scale/alpha and would fight the
      // pose scale), and swap in the attack pose's own measured ground anchor
      // so the creature's support line stays put across the swap.
      if (atkTex) {
        bob?.pause();
        sprite._extraTweens?.forEach((t) => t.pause());
        sprite.setTexture(atkTex);
        sprite.setOrigin(0.5, TEX_GROUND_ORIGIN[atkTex] ?? 1);
        sprite.setScale(atkScale);
      }
      scene.tweens.chain({
        targets: sprite,
        onComplete: () => {
          attacking = false;
          sprite.x = 0;
          if (atkTex) {
            sprite.setTexture(idleTex);
            sprite.setOrigin(0.5, TEX_GROUND_ORIGIN[idleTex] ?? 1);
            sprite.setScale(idleScale);
            bob?.resume();
            sprite._extraTweens?.forEach((t) => t.resume());
          }
        },
        tweens: [
          { x: 7, duration: 95, ease: 'Quad.easeOut' },
          { x: -20, duration: 70, ease: 'Quad.easeIn' },
          { x: 0, duration: 170, ease: 'Quad.easeOut' },
        ],
      });
    },
    hurt() {
      sprite.setAlpha(0.35);
      if (flashTimer) { flashTimer.remove(false); flashTimer = null; }
      flashTimer = scene.time.delayedCall(85, () => { sprite.setAlpha(1); flashTimer = null; });
    },
    die(onDone) {
      if (bob) bob.stop();
      sprite._extraTweens?.forEach((t) => t.stop());
      scene.tweens.add({
        targets: container,
        alpha: 0,
        y: y + 6,
        scaleY: 0.85,
        duration: 240,
        ease: 'Quad.easeIn',
        onComplete: () => {
          container.destroy();
          onDone?.();
        },
      });
    },
  };
}

/**
 * Resolve a texture key for a class+direction, falling back to front then
 * to a generated placeholder so a missing sprite never crashes the scene.
 * @param {Phaser.Scene} scene
 * @param {string} classId
 * @param {string} direction
 * @returns {string}
 */
function textureFor(scene, classId, direction) {
  const key = `${classId}_${direction}`;
  if (scene.textures.exists(key)) return key;
  const front = `${classId}_front`;
  if (scene.textures.exists(front)) return front;
  return '__missing';
}
